import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import { actions as companiesActions, selectors as companiesSelectors } from '../../../redux/reducers/companies';
// Ressources
import styles from './contracts-list-style';
import { Company, Industry } from '../../../redux/reducers/api-types';
import ErrorMessage from '../../../components/error-message/error-message';
import { generateOptions } from '../../../utils/businessUtils';
import StringUtils from '../../../utils/stringUtils';
import { getFindUsersAccountManagerURL } from '../../../utils/core-api';
import { ActionCustom, ActionFunction, FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
import Dialog from '../../../components/dialog/dialog';
import { FormControl, Icon, Input, Select, Snackbar, SnackbarCloseReason } from '@material-ui/core';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { AutoFill } from '../../../components/field-autofill';
import { UpdateCompaniesKamToKamPayload } from '../../../redux/reducers/ws-types';
import { SNACKBAR_DURATION } from '../../../utils/constants';
import CustomSnackbar from '../../../components/custom-snackbar/custom-snackbar';

interface ContractsListProps extends WithTranslation, WithStyles, RouteComponentProps {
    globalClasses: any;
    industries: Industry[];
    getIndustriesError: ErrorPayload;
    updateCompanyKAMError: ErrorPayload;
    getCompanyIndustries: () => any;
    getKamCompanyCount: (idKam: string, callback?: (dispatch, response: any) => void) => any;
    updateCompaniesKamToKam: (
        payload: UpdateCompaniesKamToKamPayload,
        callback?: (dispatch, response: any) => void,
    ) => any;
}

interface ContractsListState {
    idKam: string | null;
    newIdKam: string | null;
    idSector: string | null;
    sectorIdKam: string | null;
    companyCount: number | null;
    isOpen: boolean;
}
class ContractsListPage extends React.Component<ContractsListProps, ContractsListState> {
    tableRef: any;

    constructor(props: ContractsListProps) {
        super(props);

        this.state = {
            idKam: null,
            newIdKam: null,
            idSector: null,
            sectorIdKam: null,
            companyCount: -1,
            isOpen: false,
        };

        if (!props.industries?.length) {
            props.getCompanyIndustries();
        }
    }

    isCompanySettingsWrite: boolean = UserUtils.checkRights({
        name: AccountRight.COMPANY_SETTINGS,
        mode: RightRank.W,
    });

    generateIndustryOptions = (): PickerOption[] => {
        const { industries } = this.props;
        return generateOptions(industries, 'id');
    };

    headerBuilder = (): HeadCell[] => {
        const { t } = this.props;

        const headers: HeadCell[] = [
            createCell(t, Cell.ID, {
                idSearch: 'search[id]',
                idSort: 'id',
                sortable: true,
                formatCell: function (row) {
                    return row?.id_contract;
                },
            }),
            createCell(t, Cell.NAME, { idSearch: 'search[name]' }),
            // createCell(t, Cell.CREATION_DATE, { sortable: false }),
            // { id: 'n_user', label: t('nb_users'), filterable: false, sortable: false },
            {
                id: 'uuid_account_manager',
                idSearch: 'search[uuid_account_manager]',
                label: t('account_manager'),
                sortable: false,
                type: FormatType.Autocomplete,
                api: getFindUsersAccountManagerURL(true),
                isUuid: true,
                customAlt: (item) => item?.id,
                formatCell: function (row) {
                    const { account_manager } = row;
                    return StringUtils.userFullname(account_manager);
                },
            },
            {
                id: 'uuid_industry',
                idSearch: 'search[uuid_industry]',
                label: t('industry'),
                type: FormatType.Picker,
                pickerOptions: this.generateIndustryOptions(),
                sortable: false,
                formatCell: function (row: Company) {
                    return row.industry?.name;
                },
            },
        ];
        return headers;
    };

    handleAction(fields: any): void {
        const { idKam, newIdKam, sectorIdKam, idSector } = this.state;
        if (newIdKam && idKam) {
            const payload: UpdateCompaniesKamToKamPayload = {
                filter: {
                    account_manager_ids: [idKam],
                },
                account_manager: {
                    id: newIdKam,
                },
            };
            console.log(payload);
            this.props.updateCompaniesKamToKam(payload, () => {
                this.setState({ idKam: null, newIdKam: null, companyCount: -1 });
                this.handleClick();
                this.tableRef?.reload();
            });
        } else if (sectorIdKam && idSector) {
            const payload: UpdateCompaniesKamToKamPayload = {
                filter: {
                    industry_ids: [idSector],
                },
                account_manager: {
                    id: sectorIdKam,
                },
            };
            console.log(payload);
            this.props.updateCompaniesKamToKam(payload, () => {
                this.setState({ sectorIdKam: null, idSector: null });
                this.handleClick();
                this.tableRef?.reload();
            });
        }
    }

    resetForms(): void {
        this.setState({ idKam: null, newIdKam: null, idSector: null, sectorIdKam: null, companyCount: -1 });
    }

    renderActions = (): (ActionCustom | ActionFunction)[] => {
        const { t, classes } = this.props;
        const { idKam, newIdKam, idSector, sectorIdKam, companyCount } = this.state;

        const actions: (ActionCustom | ActionFunction)[] = [];

        if (this.isCompanySettingsWrite) {
            actions.push({
                label: t('booking:kam.kamToKam'),
                customView: (
                    <Dialog
                        className={classes.dialog}
                        btnTitle={t('booking:kam.bulkAction.title')}
                        description={t('booking:kam.bulkAction.kamToKamDesc')}
                        customButton={<div>{t('booking:kam.kamToKam')}</div>}
                        disableOK={!idKam || !newIdKam || idKam === newIdKam || companyCount <= 0}
                        onKO={this.resetForms.bind(this)}
                        formComponent={
                            <>
                                <div className={classes.dialogContent}>
                                    <AutoFill
                                        datas={{
                                            id: 'kam',
                                            label: t('booking:kam.oldKam'),
                                            minChar: 1,
                                            style: { width: '100%' },
                                            formData: this.state,
                                            variant: 'outlined',
                                            isUuid: true,
                                            onChange: (idKam) => {
                                                this.setState({ companyCount: -1 });
                                                if (idKam !== -1 && idKam) {
                                                    this.setState({ idKam });
                                                    this.props.getKamCompanyCount(idKam, (dispatch, { payload }) => {
                                                        this.setState({ companyCount: payload?.company?.total });
                                                    });
                                                }
                                            },
                                            loaderPosition: 'right',
                                            api: getFindUsersAccountManagerURL(true),
                                        }}
                                    />
                                    <Icon fontSize="medium">arrow_forward</Icon>
                                    <AutoFill
                                        datas={{
                                            id: 'newKam',
                                            label: t('booking:kam.newKam'),
                                            minChar: 1,
                                            style: { width: '100%' },
                                            formData: this.state,
                                            variant: 'outlined',
                                            isUuid: true,
                                            onChange: (newIdKam) => {
                                                if (newIdKam !== -1 && newIdKam) {
                                                    this.setState({ newIdKam });
                                                }
                                            },
                                            loaderPosition: 'right',
                                            api: getFindUsersAccountManagerURL(true),
                                        }}
                                    />
                                </div>
                                {idKam && companyCount > -1 && (
                                    <div className={classes.infoBox}>
                                        <Icon fontSize="small">work_outline</Icon>
                                        {t('booking:kam.bulkAction.nbCompanies', { count: companyCount || 0 })}
                                    </div>
                                )}
                            </>
                        }
                        onOK={(fields) => this.handleAction(fields)}
                    />
                ),
            });

            actions.push({
                label: t('booking:kam.sectorToKam'),
                customView: (
                    <Dialog
                        className={classes.dialog}
                        btnTitle={t('booking:kam.bulkAction.title')}
                        description={t('booking:kam.bulkAction.sectorToKamDesc')}
                        customButton={<div>{t('booking:kam.sectorToKam')}</div>}
                        disableOK={!idSector || !sectorIdKam}
                        onKO={this.resetForms.bind(this)}
                        formComponent={
                            <>
                                <div className={classes.dialogContent}>
                                    <Select
                                        native
                                        id={'idSector'}
                                        name={'idSector'}
                                        value={idSector || ''}
                                        // inputProps={{ form }}
                                        variant="outlined"
                                        required={true}
                                        disabled={false}
                                        onChange={(e) => {
                                            const idSector = e.target.value as any;
                                            if (idSector !== -1 && idSector) {
                                                this.setState({ idSector });
                                            }
                                        }}
                                    >
                                        {this.generateIndustryOptions().map((select, i) => {
                                            return (
                                                <option key={select.value} value={select.value}>
                                                    {select.label}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                    <Icon fontSize="medium">arrow_forward</Icon>
                                    <AutoFill
                                        datas={{
                                            id: 'newKam',
                                            label: t('booking:kam.newKam'),
                                            minChar: 1,
                                            style: { width: '100%' },
                                            formData: this.state,
                                            variant: 'outlined',
                                            isUuid: true,
                                            onChange: (sectorIdKam) => {
                                                if (sectorIdKam !== -1 && sectorIdKam) {
                                                    this.setState({ sectorIdKam });
                                                }
                                            },
                                            loaderPosition: 'right',
                                            api: getFindUsersAccountManagerURL(true),
                                        }}
                                    />
                                </div>
                                {idKam && companyCount > -1 && (
                                    <div className={classes.infoBox}>
                                        <Icon fontSize="small">work_outline</Icon>
                                        {t('booking:kam.bulkAction.nbCompanies', { count: companyCount || 0 })}
                                    </div>
                                )}
                            </>
                        }
                        onOK={(fields) => this.handleAction(fields)}
                    />
                ),
            });
        }
        return actions;
    };

    handleClick = () => {
        this.setState({ isOpen: true });
    };

    handleClose = (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === 'clickaway') {
            return;
        }

        this.setState({ isOpen: false });
    };

    render() {
        const { t, classes, getIndustriesError, updateCompanyKAMError } = this.props;
        const { isOpen } = this.state;

        return (
            <App
                disableBreadcrumb
                header={{
                    title: t('title'),
                    subtitle: t('description'),
                    actions: this.renderActions(),
                }}
            >
                <ErrorMessage payload={getIndustriesError || updateCompanyKAMError} />
                <CustomSnackbar
                    open={isOpen}
                    onClose={this.handleClose}
                    message={t('booking:kam.bulkAction.done')}
                    mode="success"
                />
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.headerBuilder()}
                    filterVisible={false}
                    type={tableRequestPoints.CONTRACTS}
                />
            </App>
        );
    }
}

const mapStateToProps = (state) => ({
    industries: companiesSelectors.getIndustriesNew(state),
    getIndustriesError: errorsSelectors.getIndustriesError(state),
    updateCompanyKAMError: errorsSelectors.updateCompanyKAM(state),
});

const mapDispatchToProps = {
    getCompanyIndustries: companiesActions.getCompanyNewIndustries,
    getKamCompanyCount: companiesActions.getKamCompanyCount,
    updateCompaniesKamToKam: companiesActions.updateCompaniesKamToKam,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(withTranslation(['company', 'common'])(ContractsListPage)));
